import React, { useState } from "react";
import { isEmail, MAX_RECIPIENTS } from "./alertLabels";
import { smallButton } from "../template/templateLabels";

// One mail the backend sends the staff: on or off, and who gets it. A mail that is
// turned off keeps its list for when it is turned back on.

const fieldClass =
	"block w-full h-9 px-3 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 focus:border-purple-400 focus:ring focus:ring-purple-200 focus:ring-opacity-50 focus:outline-none dark:focus:ring-gray-500";

export const Toggle = ({ checked, onChange, label }) => (
	<button
		type="button"
		role="switch"
		aria-checked={checked}
		aria-label={label}
		onClick={() => onChange(!checked)}
		className={
			"relative inline-flex flex-shrink-0 h-6 transition-colors duration-200 border-2 border-transparent rounded-full cursor-pointer w-11 focus:outline-none focus:ring-2 focus:ring-purple-200 " +
			(checked ? "bg-purple-600" : "bg-gray-300 dark:bg-gray-600")
		}
	>
		<span
			aria-hidden="true"
			className={
				"inline-block w-5 h-5 transition duration-200 transform bg-white rounded-full shadow " +
				(checked ? "translate-x-5" : "translate-x-0")
			}
		/>
	</button>
);

// Several addresses may be pasted at once
const splitEmails = (text) =>
	text
		.split(/[\s,;]+/)
		.map((item) => item.trim().toLowerCase())
		.filter(Boolean);

const AlertCard = ({ alert, mail, admins, onChange, testState, onTest }) => {
	const [draft, setDraft] = useState("");
	const [draftError, setDraftError] = useState(null);

	const adminOf = (email) => admins.find((item) => item.email === email);
	const addableAdmins = admins.filter((item) => !mail.to.includes(item.email));
	const full = mail.to.length >= MAX_RECIPIENTS;

	const remove = (email) => onChange({ ...mail, to: mail.to.filter((item) => item !== email) });

	const add = (emails) => {
		const to = [...mail.to];
		for (const email of emails) {
			if (!to.includes(email)) to.push(email);
		}
		if (to.length > MAX_RECIPIENTS) {
			setDraftError(`${MAX_RECIPIENTS}-оос олон хүн нэмэх боломжгүй`);
			return false;
		}
		onChange({ ...mail, to });
		return true;
	};

	const addDraft = () => {
		const emails = splitEmails(draft);
		if (emails.length === 0) return;
		const wrong = emails.filter((email) => !isEmail(email));
		if (wrong.length > 0) {
			setDraftError(`${wrong.join(", ")} имэйл хаяг биш байна`);
			return;
		}
		if (add(emails)) {
			setDraft("");
			setDraftError(null);
		}
	};

	const onKeyDown = (e) => {
		if (e.key === "Enter" || e.key === ",") {
			e.preventDefault();
			addDraft();
		} else if (e.key === "Backspace" && draft === "" && mail.to.length > 0) {
			remove(mail.to[mail.to.length - 1]);
		}
	};

	const empty = mail.enabled && mail.to.length === 0;

	return (
		<div
			className={
				"bg-white rounded-lg shadow-sm dark:bg-gray-800 border " +
				(empty ? "border-red-300 dark:border-red-700" : "border-transparent")
			}
		>
			<div className="flex items-start gap-4 p-5">
				<span
					className={
						"flex items-center justify-center flex-shrink-0 w-10 h-10 rounded-lg " +
						(mail.enabled
							? alert.tint
							: "text-gray-400 bg-gray-100 dark:text-gray-500 dark:bg-gray-700")
					}
					aria-hidden="true"
				>
					<i className={"fas " + alert.icon} />
				</span>
				<div className="flex-1 min-w-0">
					<div className="flex flex-wrap items-center gap-2">
						<h4 className="font-semibold text-gray-700 dark:text-gray-200">{alert.name}</h4>
						{!mail.enabled && (
							<span className="px-2 py-0.5 text-xs text-gray-600 bg-gray-100 rounded-full dark:text-gray-300 dark:bg-gray-700">
								Унтраастай
							</span>
						)}
					</div>
					<p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{alert.text}</p>
				</div>
				<Toggle
					checked={mail.enabled}
					onChange={(enabled) => onChange({ ...mail, enabled })}
					label={alert.name + " мэйл"}
				/>
			</div>

			<div
				className={
					"px-5 pb-5 transition-opacity duration-150" + (mail.enabled ? "" : " opacity-60")
				}
			>
				<div className="flex flex-wrap items-center justify-between gap-2 mb-2">
					<p className="text-xs font-medium tracking-wide text-gray-500 uppercase dark:text-gray-400">
						Хүлээн авагч ({mail.to.length})
					</p>
					<button
						type="button"
						onClick={onTest}
						disabled={!testState.can || testState.busy}
						title={testState.reason || "Хадгалсан хүлээн авагчид руу туршилтын мэйл илгээнэ"}
						className={
							smallButton +
							" inline-flex items-center whitespace-nowrap" +
							(!testState.can || testState.busy ? " opacity-50 cursor-not-allowed" : "")
						}
					>
						<i
							className={"mr-1.5 fas " + (testState.busy ? "fa-spinner fa-spin" : "fa-paper-plane")}
						/>
						Туршилтын мэйл
					</button>
				</div>
				<div className="flex flex-wrap gap-2">
					{mail.to.map((email) => {
						const admin = adminOf(email);
						return (
							<span
								key={email}
								className="inline-flex items-center max-w-full py-1 pl-1 pr-1 text-sm text-gray-700 bg-gray-100 rounded-full dark:text-gray-200 dark:bg-gray-700"
								title={email}
							>
								<span
									className={
										"flex items-center justify-center flex-shrink-0 w-6 h-6 text-xs font-semibold uppercase rounded-full " +
										(admin
											? "text-purple-600 bg-purple-100 dark:text-purple-200 dark:bg-purple-900"
											: "text-gray-500 bg-white dark:text-gray-300 dark:bg-gray-600")
									}
									aria-hidden="true"
								>
									{admin ? (
										(admin.name || "?").trim().charAt(0)
									) : (
										<i className="text-xs fas fa-at" />
									)}
								</span>
								<span className="min-w-0 ml-2 truncate">
									{admin ? (
										<>
											<span className="font-medium">{admin.name}</span>
											<span className="hidden ml-1 text-xs text-gray-500 sm:inline dark:text-gray-400">
												{email}
											</span>
										</>
									) : (
										email
									)}
								</span>
								<button
									type="button"
									onClick={() => remove(email)}
									className="flex items-center justify-center flex-shrink-0 w-6 h-6 ml-1 text-gray-400 rounded-full hover:text-red-600 hover:bg-white dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-200"
									aria-label={email + " хасах"}
								>
									<i className="text-xs fas fa-times" />
								</button>
							</span>
						);
					})}
					{mail.to.length === 0 && (
						<span
							className={
								"text-sm " +
								(empty ? "text-red-600 dark:text-red-400" : "text-gray-500 dark:text-gray-400")
							}
						>
							{empty ? "Хүлээн авагч алга. Нэмэх эсвэл мэйлийг унтраана уу." : "Хүлээн авагч алга"}
						</span>
					)}
				</div>

				<div className="flex flex-col gap-2 mt-4 sm:flex-row">
					<div className="flex flex-1 min-w-0 gap-2">
						<input
							type="email"
							value={draft}
							onChange={(e) => {
								setDraft(e.target.value);
								setDraftError(null);
							}}
							onKeyDown={onKeyDown}
							onBlur={() => draft.trim() && addDraft()}
							placeholder="Имэйл хаяг бичээд Enter"
							disabled={full}
							className={fieldClass + (draftError ? " border-red-400 dark:border-red-500" : "")}
						/>
						<button
							type="button"
							onClick={addDraft}
							disabled={full || draft.trim() === ""}
							className={
								smallButton +
								" h-9 whitespace-nowrap" +
								(full || draft.trim() === "" ? " opacity-50 cursor-not-allowed" : "")
							}
						>
							Нэмэх
						</button>
					</div>
					<select
						value=""
						onChange={(e) => e.target.value && add([e.target.value])}
						disabled={full || addableAdmins.length === 0}
						className={
							fieldClass + " sm:w-48 pr-8" + (addableAdmins.length === 0 ? " opacity-50" : "")
						}
						aria-label="Админаас нэмэх"
					>
						<option value="">
							{addableAdmins.length === 0 ? "Бүх админ нэмэгдсэн" : "Админаас нэмэх…"}
						</option>
						{addableAdmins.map((item) => (
							<option key={item._id} value={item.email}>
								{item.name} · {item.email}
							</option>
						))}
					</select>
				</div>
				{draftError && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{draftError}</p>}
			</div>
		</div>
	);
};

export default AlertCard;
